import { useEffect, useState } from 'react';
import { X, PartyPopper, Sparkles, Star } from 'lucide-react';

interface CelebrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * CelebrationModal - Shows a celebration popup with CSS confetti animation
 * when the user completes the onboarding tutorial
 */
export function CelebrationModal({ isOpen, onClose }: CelebrationModalProps) {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Start confetti animation after a short delay
      const timeout = setTimeout(() => setShowConfetti(true), 100);
      return () => clearTimeout(timeout);
    } else {
      setShowConfetti(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleComplete = () => {
    window.dispatchEvent(new CustomEvent('onboarding:widget-expand'));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleComplete}
      />

      {/* Confetti particles */}
      {showConfetti && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="confetti-particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                backgroundColor: [
                  '#10b981', // emerald
                  '#f59e0b', // amber
                  '#3b82f6', // blue
                  '#ec4899', // pink
                  '#8b5cf6', // violet
                  '#ef4444', // red
                ][Math.floor(Math.random() * 6)],
              }}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={handleComplete}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        >
          <X className="w-5 h-5 text-zinc-400" />
        </button>

        {/* Content */}
        <div className="p-8 text-center">
          {/* Icon with animation */}
          <div className="relative inline-flex items-center justify-center mb-6">
            <div className="absolute inset-0 animate-ping-slow">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20" />
            </div>
            <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-500/30">
              <PartyPopper className="w-10 h-10 text-white" />
            </div>
            {/* Floating stars */}
            <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-amber-400 animate-bounce" />
            <Star className="absolute -bottom-1 -left-3 w-5 h-5 text-emerald-400 animate-pulse" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
            🎉 Félicitations !
          </h2>

          {/* Description */}
          <p className="text-zinc-600 dark:text-zinc-400 mb-6">
            Vous avez terminé le tutoriel avec succès !<br />
            Votre agent est maintenant prêt à vous assister.
          </p>

          {/* CTA Button */}
          <button
            onClick={handleComplete}
            className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:from-emerald-400 hover:to-emerald-500 transition-all"
          >
            Terminer le tutoriel
          </button>
        </div>
      </div>

      {/* CSS for confetti animation */}
      <style>{`
        .confetti-particle {
          position: absolute;
          top: -10px;
          width: 10px;
          height: 10px;
          border-radius: 2px;
          animation: confetti-fall 3s ease-out forwards;
        }

        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 0.5;
          }
          75%, 100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default CelebrationModal;
